(() => {
  if (window.MyPCCommerceUXV9) {
    window.MyPCCommerceUXV9.init(document);
    return;
  }

  const calculatorSelector = '[data-mypc-mixed-compact]';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const money = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
  const integer = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });
  const formatMoney = (cents) => money.format(Math.round(Number(cents || 0) / 100));
  const formatAmountInput = (cents) => integer.format(Math.round(Number(cents || 0) / 100));
  const parseAmountInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    const pesos = Number(digits || 0);
    return Number.isSafeInteger(pesos) ? pesos * 100 : 0;
  };
  const formatPercent = (value) => {
    const rounded = Math.round(Number(value || 0) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',');
  };
  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  class CompactMixedPayment {
    constructor(root) {
      this.root = root;
      this.range = root.querySelector('[data-mixed-range]');
      this.transferPercent = root.querySelector('[data-mixed-transfer-percent]');
      this.cardPercent = root.querySelector('[data-mixed-card-percent]');
      this.transferInput = root.querySelector('[data-mixed-transfer-input]');
      this.savings = root.querySelector('[data-mixed-savings]');
      this.cardInput = root.querySelector('[data-mixed-card-input]');
      this.total = root.querySelector('[data-mixed-total]');
      this.installment = root.querySelector('[data-mixed-installment]');
      this.whatsapp = root.querySelector('[data-mixed-whatsapp]');
      this.coach = root.querySelector('[data-mixed-coach]');
      this.coachText = this.coach?.querySelector('p');
      this.announcer = root.querySelector('[data-mixed-announcer]');
      this.context = root.dataset.mixedContext || 'product';
      this.priceCents = Number(root.dataset.priceCents || 0);
      this.title = root.dataset.paymentTitle || '';
      this.variantTitle = root.dataset.paymentVariant || '';
      this.reference = root.dataset.paymentReference || '';
      this.financingFee = Number(root.dataset.financingFeePercent || document.documentElement.dataset.mypcFinancingFee || 9.65);
      this.installments = Math.max(1, Number(root.dataset.mpInstallments || document.documentElement.dataset.mypcMpInstallments || 12));
      this.whatsappNumber = root.dataset.whatsappNumber || '56935972543';
      this.demoToken = 0;
      this.demoTimer = 0;
      this.hasInteracted = false;
      this.announceTimer = 0;
      this.inputMode = 'percentage';
      this.manualTransferCents = 0;
      this.manualCardCents = 0;
      this.rangeFrame = 0;
      this.valueEffectTimer = 0;

      if (!this.range) return;
      this.bind();
      this.render(Number(this.range.value), false);
      this.setupDemo();
    }

    getBasePrice() {
      if (this.context === 'cart') {
        const items = [...document.querySelectorAll('[data-mypc-cart-item]')];
        const calculated = items.reduce((sum, item) => {
          const unitPrice = Number(item.dataset.mypcUnitPrice || 0);
          const quantity = Math.max(0, Number(item.querySelector('input[name="updates[]"]')?.value || 0));
          return sum + unitPrice * quantity;
        }, 0);
        return items.length ? calculated : this.priceCents;
      }

      const productRoot = this.root.closest('[data-product-root]');
      const quantity = Math.max(1, Number(productRoot?.querySelector('form.product-form input[name="quantity"]')?.value || 1));
      return this.priceCents * quantity;
    }

    buildCalculation(basePrice, transferBase, transferAmount, cardAmount) {
      const safeBasePrice = Math.max(0, Math.round(basePrice));
      const safeTransferBase = clamp(Math.round(transferBase), 0, safeBasePrice);
      const safeTransferAmount = Math.max(0, Math.round(transferAmount));
      const safeCardAmount = clamp(Math.round(cardAmount), 0, safeBasePrice);
      const percentage = safeBasePrice > 0 ? safeTransferBase * 100 / safeBasePrice : 0;
      const cardPercentage = 100 - percentage;
      return {
        percentage,
        cardPercentage,
        basePrice: safeBasePrice,
        transferBase: safeTransferBase,
        transferAmount: safeTransferAmount,
        savings: Math.max(0, safeTransferBase - safeTransferAmount),
        cardAmount: safeCardAmount,
        total: safeTransferAmount + safeCardAmount,
        installment: Math.round(safeCardAmount / this.installments)
      };
    }

    calculate(transferPercent) {
      const percentage = clamp(Math.round(Number(transferPercent || 0)), 0, 100);
      const basePrice = this.getBasePrice();
      const transferBase = Math.round(basePrice * percentage / 100);
      const transferAmount = Math.round(transferBase * (100 - this.financingFee) / 100);
      const cardAmount = Math.max(0, basePrice - transferBase);
      return this.buildCalculation(basePrice, transferBase, transferAmount, cardAmount);
    }

    calculateFromTransferAmount(transferAmount) {
      const basePrice = this.getBasePrice();
      const multiplier = clamp((100 - this.financingFee) / 100, 0.0001, 1);
      const maximumTransfer = Math.round(basePrice * multiplier);
      const exactTransfer = clamp(Math.round(Number(transferAmount || 0)), 0, maximumTransfer);
      const transferBase = clamp(Math.round(exactTransfer / multiplier), 0, basePrice);
      const cardAmount = Math.max(0, basePrice - transferBase);
      return this.buildCalculation(basePrice, transferBase, exactTransfer, cardAmount);
    }

    calculateFromCardAmount(cardAmount) {
      const basePrice = this.getBasePrice();
      const exactCard = clamp(Math.round(Number(cardAmount || 0)), 0, basePrice);
      const transferBase = Math.max(0, basePrice - exactCard);
      const transferAmount = Math.round(transferBase * (100 - this.financingFee) / 100);
      return this.buildCalculation(basePrice, transferBase, transferAmount, exactCard);
    }

    setRangeVisual(percentage) {
      const safePercentage = clamp(Number(percentage || 0), 0, 100);
      this.range.value = String(Math.round(safePercentage));
      this.range.style.setProperty('--mypc-mixed-percent', `${safePercentage}%`);
    }

    animateRangeVisual(percentage) {
      const target = clamp(Number(percentage || 0), 0, 100);
      window.cancelAnimationFrame(this.rangeFrame);
      if (reducedMotion()) {
        this.setRangeVisual(target);
        return;
      }

      const currentStyle = Number.parseFloat(this.range.style.getPropertyValue('--mypc-mixed-percent'));
      const start = Number.isFinite(currentStyle) ? currentStyle : Number(this.range.value || 0);
      const distance = target - start;
      if (Math.abs(distance) < 0.1) {
        this.setRangeVisual(target);
        return;
      }

      const startedAt = performance.now();
      const duration = clamp(220 + Math.abs(distance) * 4, 260, 520);
      const frame = (now) => {
        const progress = clamp((now - startedAt) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.setRangeVisual(start + distance * eased);
        if (progress < 1) this.rangeFrame = window.requestAnimationFrame(frame);
      };
      this.rangeFrame = window.requestAnimationFrame(frame);
    }

    animateValueChange(mode) {
      window.clearTimeout(this.valueEffectTimer);
      this.root.classList.remove('is-value-changing', 'is-editing-transfer', 'is-editing-card');
      void this.root.offsetWidth;
      this.root.classList.add('is-value-changing', mode === 'transfer' ? 'is-editing-transfer' : 'is-editing-card');
      this.valueEffectTimer = window.setTimeout(() => {
        this.root.classList.remove('is-value-changing', 'is-editing-transfer', 'is-editing-card');
      }, 480);
    }

    renderValues(values, announce, activeInput) {
      if (activeInput) this.animateRangeVisual(values.percentage);
      else this.setRangeVisual(values.percentage);
      this.range.setAttribute('aria-valuetext', `${formatPercent(values.percentage)}% por transferencia y ${formatPercent(values.cardPercentage)}% por tarjeta de crédito`);
      this.transferPercent.textContent = `${formatPercent(values.percentage)}%`;
      this.cardPercent.textContent = `${formatPercent(values.cardPercentage)}%`;
      if (this.transferInput && activeInput !== this.transferInput) this.transferInput.value = formatAmountInput(values.transferAmount);
      this.savings.textContent = formatMoney(values.savings);
      if (this.cardInput && activeInput !== this.cardInput) this.cardInput.value = formatAmountInput(values.cardAmount);
      this.total.textContent = formatMoney(values.total);
      this.installment.textContent = formatMoney(values.installment);
      this.updateWhatsApp(values);

      if (announce && this.announcer) {
        window.clearTimeout(this.announceTimer);
        this.announceTimer = window.setTimeout(() => {
          this.announcer.textContent = `Transferencia ${formatMoney(values.transferAmount)}. Ahorro ${formatMoney(values.savings)}. Tarjeta de crédito ${formatMoney(values.cardAmount)}. Total ${formatMoney(values.total)}.`;
        }, 220);
      }
    }

    render(transferPercent, announce) {
      this.inputMode = 'percentage';
      this.renderValues(this.calculate(transferPercent), announce);
    }

    renderCurrent(announce, activeInput) {
      if (this.inputMode === 'transfer') {
        this.renderValues(this.calculateFromTransferAmount(this.manualTransferCents), announce, activeInput);
        return;
      }
      if (this.inputMode === 'card') {
        this.renderValues(this.calculateFromCardAmount(this.manualCardCents), announce, activeInput);
        return;
      }
      this.renderValues(this.calculate(Number(this.range.value)), announce, activeInput);
    }

    updateWhatsApp(values) {
      if (!this.whatsapp) return;
      const cartItems = [...document.querySelectorAll('[data-mypc-cart-item]')]
        .map((item) => {
          const quantity = Math.max(0, Number(item.querySelector('input[name="updates[]"]')?.value || 0));
          if (!quantity) return '';
          const title = item.dataset.mypcItemTitle || item.dataset.mypcItemReference || 'Producto';
          const variant = item.dataset.mypcItemVariant;
          const reference = item.dataset.mypcItemReference;
          const variantText = variant && variant.toLowerCase() !== 'default title' ? ` · ${variant}` : '';
          const referenceText = reference && reference !== title ? ` · SKU ${reference}` : '';
          return `${title}${variantText}${referenceText} × ${quantity}`;
        })
        .filter(Boolean);

      const productRoot = this.root.closest('[data-product-root]');
      const productQuantity = Math.max(1, Number(productRoot?.querySelector('form.product-form input[name="quantity"]')?.value || 1));
      const productVariant = this.variantTitle && this.variantTitle.toLowerCase() !== 'default title' ? ` · ${this.variantTitle}` : '';
      const productReference = this.reference ? ` · SKU ${this.reference}` : '';
      const productLine = `${this.title || 'Producto seleccionado'}${productVariant}${productReference} × ${productQuantity}`;
      const itemLines = this.context === 'cart'
        ? (cartItems.length ? cartItems : [this.reference || 'Productos del carrito'])
        : [productLine];
      const productsHeading = this.context === 'cart' ? '*Productos del pedido*' : '*PC seleccionado*';
      const transferText = values.transferAmount > 0 ? `• Transferencia: *${formatMoney(values.transferAmount)}*` : '';
      const cardText = values.cardAmount > 0 ? `• Saldo con tarjeta de crédito (Mercado Pago): *${formatMoney(values.cardAmount)}*` : '';
      const installmentText = values.cardAmount > 0 ? `• ${this.installments} cuotas sin interés: *${formatMoney(values.installment)} cada una*` : '';
      const message = [
        'Hola, quiero coordinar un *Pago Flexible MyPC*.',
        '',
        productsHeading,
        ...itemLines.map((item) => `• ${item}`),
        '',
        '*Forma de pago*',
        transferText,
        cardText,
        installmentText,
        `• Total final: *${formatMoney(values.total)}*`,
        '',
        values.cardAmount > 0
          ? '¿Me envían el link de Mercado Pago para completar el saldo con tarjeta de crédito?'
          : '¿Me ayudan a coordinar la transferencia?'
      ].filter((line) => line !== '').join('\n');
      this.whatsapp.href = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(message)}`;
    }

    bind() {
      const stopDemo = () => this.cancelDemo();
      this.range.addEventListener('pointerdown', stopDemo);
      this.range.addEventListener('touchstart', stopDemo, { passive: true });
      this.range.addEventListener('keydown', stopDemo);
      this.range.addEventListener('input', () => {
        this.cancelDemo();
        this.render(Number(this.range.value), true);
      });

      const bindAmountInput = (input, mode) => {
        if (!input) return;
        input.addEventListener('focus', () => {
          this.cancelDemo();
          window.setTimeout(() => input.select(), 0);
        });
        input.addEventListener('pointerdown', stopDemo);
        input.addEventListener('input', () => {
          this.cancelDemo();
          this.inputMode = mode;
          if (mode === 'transfer') this.manualTransferCents = parseAmountInput(input.value);
          else this.manualCardCents = parseAmountInput(input.value);
          this.animateValueChange(mode);
          this.renderCurrent(true, input);
        });
        input.addEventListener('blur', () => this.renderCurrent(false));
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            input.blur();
          }
        });
      };

      bindAmountInput(this.transferInput, 'transfer');
      bindAmountInput(this.cardInput, 'card');

      const quantityScope = this.context === 'cart' ? document.querySelector('#CartFormMyPC') : this.root.closest('[data-product-root]');
      quantityScope?.addEventListener('input', (event) => {
        if (event.target.matches('input[name="quantity"], input[name="updates[]"]')) this.renderCurrent(false);
      });
      quantityScope?.addEventListener('change', (event) => {
        if (event.target.matches('input[name="quantity"], input[name="updates[]"]')) this.renderCurrent(false);
      });
      quantityScope?.addEventListener('click', (event) => {
        if (event.target.closest('[data-quantity-change]')) window.setTimeout(() => this.renderCurrent(false));
      });
    }

    updateVariant(variant) {
      if (this.context !== 'product' || !variant) return;
      this.priceCents = Number(variant.price || 0);
      this.variantTitle = variant.title || '';
      this.reference = variant.sku || `variante ${variant.id}`;
      this.root.dataset.priceCents = String(this.priceCents);
      this.root.dataset.paymentVariant = this.variantTitle;
      this.root.dataset.paymentReference = this.reference;
      this.renderCurrent(false);
    }

    changeCoach(text) {
      if (!this.coach || !this.coachText) return;
      this.coach.classList.add('is-changing');
      window.setTimeout(() => {
        this.coachText.textContent = text;
        this.coach.classList.remove('is-changing');
      }, reducedMotion() ? 0 : 150);
    }

    cancelDemo() {
      if (this.hasInteracted) return;
      this.hasInteracted = true;
      this.demoToken += 1;
      window.clearTimeout(this.demoTimer);
      this.root.classList.remove('is-demoing');
      this.changeCoach('Tú eliges el monto exacto o el porcentaje de cada método de pago.');
    }

    tweenTo(target, duration, token) {
      const start = Number(this.range.value);
      const difference = target - start;
      const startedAt = performance.now();
      return new Promise((resolve) => {
        const frame = (now) => {
          if (token !== this.demoToken || this.hasInteracted) return resolve(false);
          const progress = clamp((now - startedAt) / duration, 0, 1);
          const eased = .5 - Math.cos(Math.PI * progress) / 2;
          this.render(Math.round(start + difference * eased), false);
          if (progress < 1) window.requestAnimationFrame(frame);
          else resolve(true);
        };
        window.requestAnimationFrame(frame);
      });
    }

    animateBrandLogo() {
      const logo = this.root.querySelector('.mypc-mixed-compact__logo');
      if (!logo || reducedMotion()) return;
      logo.classList.remove('mypc-logo-animation--waiting', 'mypc-logo-animation--assemble');
      void logo.getBoundingClientRect();
      logo.classList.add('mypc-logo-animation--assemble');
    }

    async runDemo() {
      if (this.hasInteracted) return;
      const token = ++this.demoToken;
      this.root.classList.add('is-demoing');
      this.animateBrandLogo();
      this.changeCoach('Mira cómo cambian los montos en tiempo real.');
      if (!await this.tweenTo(80, 760, token)) return;
      this.changeCoach(`El ${String(this.financingFee).replace('.', ',')}% se descuenta solo de la parte pagada por transferencia.`);
      await wait(820);
      if (!await this.tweenTo(20, 920, token)) return;
      this.changeCoach(`La parte morada se paga con tarjeta de crédito en ${this.installments} cuotas sin interés mediante Mercado Pago.`);
      await wait(820);
      if (!await this.tweenTo(50, 760, token)) return;
      this.changeCoach('Cuanto mayor sea tu pie, menor será tu cuota mensual.');
      await wait(1100);
      if (token !== this.demoToken) return;
      this.root.classList.remove('is-demoing');
      this.changeCoach('Mueve la barra o toca un monto para escribirlo.');
    }

    setupDemo() {
      if (reducedMotion() || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.demoTimer = window.setTimeout(() => this.runDemo(), 450);
      }, { threshold: .55 });
      observer.observe(this.root);
    }
  }

  const overlayController = (() => {
    let token = 0;

    const getOverlay = () => document.querySelector('[data-mypc-commerce-overlay]');
    const setLogoAnimation = (overlay) => {
      const logo = overlay?.querySelector('[data-mypc-overlay-logo] .mypc-logo-svg');
      if (!logo || reducedMotion()) return;
      logo.classList.remove('mypc-logo-animation--assemble');
      void logo.getBoundingClientRect();
      logo.classList.add('mypc-logo-animation--assemble');
    };

    const close = async (overlay) => {
      if (!overlay || overlay.hidden) return;
      overlay.classList.remove('is-opening');
      overlay.classList.add('is-closing');
      await wait(reducedMotion() ? 0 : 220);
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-closing');
      document.body.classList.remove('mypc-commerce-overlay-open');
    };

    const play = async (steps, keepOpen = false) => {
      const overlay = getOverlay();
      if (!overlay) return true;
      const currentToken = ++token;
      const eyebrow = overlay.querySelector('[data-mypc-overlay-eyebrow]');
      const title = overlay.querySelector('[data-mypc-overlay-title]');
      const text = overlay.querySelector('[data-mypc-overlay-text]');
      const dots = [...overlay.querySelectorAll('[data-mypc-overlay-dot]')];
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.remove('is-closing');
      overlay.classList.add('is-opening');
      document.body.classList.add('mypc-commerce-overlay-open');
      setLogoAnimation(overlay);

      for (let index = 0; index < steps.length; index += 1) {
        if (currentToken !== token) return false;
        const step = steps[index];
        eyebrow.textContent = step.eyebrow || 'MyPC';
        title.textContent = step.title;
        text.textContent = step.text;
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex <= index));
        await wait(step.duration);
      }

      if (!keepOpen) await close(overlay);
      return currentToken === token;
    };

    return { play, close: () => close(getOverlay()) };
  })();

  const initializeBubble = (scope = document) => {
    const bubble = scope.querySelector?.('[data-mypc-cart-bubble]') || document.querySelector('[data-mypc-cart-bubble]');
    if (!bubble || bubble.dataset.mypcInitialized === 'true') return;
    bubble.dataset.mypcInitialized = 'true';
    let hideTimer = 0;

    const hide = () => {
      if (bubble.hidden || bubble.classList.contains('is-closing')) return;
      window.clearTimeout(hideTimer);
      bubble.classList.remove('is-visible');
      bubble.classList.add('is-closing');
      window.setTimeout(() => {
        bubble.hidden = true;
        bubble.classList.remove('is-closing');
      }, reducedMotion() ? 0 : 300);
    };

    document.addEventListener('mypc:cart-ui-added', () => {
      window.clearTimeout(hideTimer);
      bubble.hidden = false;
      bubble.classList.remove('is-closing');
      void bubble.getBoundingClientRect();
      bubble.classList.add('is-visible');
      hideTimer = window.setTimeout(hide, 9000);
    });

    bubble.querySelector('[data-mypc-cart-bubble-close]')?.addEventListener('click', hide);
    bubble.querySelector('[data-mypc-go-cart]')?.addEventListener('click', async (event) => {
      event.preventDefault();
      const destination = event.currentTarget.href;
      hide();
      await overlayController.play([
        { eyebrow: 'Compra agregada', title: '¡Gracias!', text: 'Tu producto ya está seguro en el carrito.', duration: 650 },
        { eyebrow: 'MyPC', title: 'Redirigiendo al carrito', text: 'Preparando el resumen de tu pedido…', duration: 1050 }
      ], true);
      window.location.assign(destination);
    });
  };

  let paymentTutorialBusy = false;

  const initializeCartActions = (scope = document) => {
    const mpInstallments = Math.max(1, Number(document.documentElement.dataset.mypcMpInstallments || 12));

    const activateOriginalCheckout = (button) => {
      if (!button) return;
      button.dataset.mypcCheckoutBypass = 'true';
      button.click();
      window.setTimeout(() => delete button.dataset.mypcCheckoutBypass, 0);
    };

    const cardButton = scope.querySelector?.('.cart-checkout-button[name="checkout"]') || document.querySelector('.cart-checkout-button[name="checkout"]');
    if (cardButton && cardButton.dataset.mypcCardAnimationInitialized !== 'true') {
      cardButton.dataset.mypcCardAnimationInitialized = 'true';
      cardButton.addEventListener('click', async (event) => {
        if (cardButton.dataset.mypcCheckoutBypass === 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (paymentTutorialBusy) return;
        paymentTutorialBusy = true;
        cardButton.classList.add('is-busy');
        cardButton.setAttribute('aria-busy', 'true');
        const completed = await overlayController.play([
          { eyebrow: 'Pago en cuotas', title: 'ELIGE LA OPCIÓN DE PAGO EN CUOTAS', text: 'Realiza tu pedido seleccionando el método de pago en cuotas.', duration: 3500 },
          { eyebrow: 'WhatsApp MyPC', title: 'CONTACTA A MYPC POR WHATSAPP', text: 'Te enviaremos tu Link de Pago Mercado Pago para pagar con tarjeta de crédito.', duration: 3500 },
          { eyebrow: 'Flexibilidad', title: 'PUEDES USAR MÁS DE UNA TARJETA', text: 'Si lo necesitas, puedes solicitar varios Links de Pago y dividir el pago entre distintas tarjetas.', duration: 3500 }
        ]);
        cardButton.classList.remove('is-busy');
        cardButton.removeAttribute('aria-busy');
        paymentTutorialBusy = false;
        if (completed) activateOriginalCheckout(cardButton);
      });
    }

    const mixedButton = scope.querySelector?.('[data-mypc-multi-checkout]') || document.querySelector('[data-mypc-multi-checkout]');
    if (mixedButton && mixedButton.dataset.mypcInitialized !== 'true') {
      mixedButton.dataset.mypcInitialized = 'true';
      mixedButton.addEventListener('click', async () => {
        if (paymentTutorialBusy) return;
        const form = mixedButton.closest('form');
        const originalCheckout = form?.querySelector('.cart-checkout-button[name="checkout"]');
        if (!form || !originalCheckout) return;
        paymentTutorialBusy = true;
        mixedButton.classList.add('is-busy');
        mixedButton.setAttribute('aria-busy', 'true');
        const completed = await overlayController.play([
          { eyebrow: 'Pago Flexible MyPC', title: 'PASO 1', text: 'Llena tus datos correctamente.', duration: 3500 },
          { eyebrow: 'Pago Flexible MyPC', title: 'PASO 2', text: 'Elige el método de pago llamado Pago Flexible y realiza el pedido.', duration: 3500 },
          { eyebrow: 'Pago Flexible MyPC', title: 'PASO 3', text: 'Contacta a MyPC por WhatsApp y un ejecutivo te ayudará a pagar como más te guste.', duration: 3500 }
        ]);
        mixedButton.classList.remove('is-busy');
        mixedButton.removeAttribute('aria-busy');
        paymentTutorialBusy = false;
        if (completed) activateOriginalCheckout(originalCheckout);
      });
    }


  };

  const init = (scope = document) => {
    const calculators = scope.matches?.(calculatorSelector)
      ? [scope]
      : [...(scope.querySelectorAll?.(calculatorSelector) || [])];
    calculators.forEach((root) => {
      if (root.mypcCompactMixedPayment) return;
      root.mypcCompactMixedPayment = new CompactMixedPayment(root);
    });
    initializeBubble(scope);
    initializeCartActions(scope);
  };

  window.MyPCCommerceUXV9 = { init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);

  document.addEventListener('variant:change', (event) => {
    document.querySelectorAll(calculatorSelector).forEach((root) => root.mypcCompactMixedPayment?.updateVariant(event.detail?.variant));
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
